import { FormSection, FormRow, MediumFormRow } from 'framework/ui/layout';
import {
  Checkbox,
  DateInput,
  NumberInput,
  TextInput,
  ClearAfterUnmountWrapper,
  DropdownInputOnlyBoolean,
} from 'framework/form';
import RolesDropdown from 'domain/shared/assignment/createupdate/RolesDropdown';
import AssignmentServiceCatalogTargetInput from 'domain/shared/assignment/target/shared/input/AssignmentServiceCatalogTargetInput';
import AssignmentTypeInput from 'domain/shared/assignment/input/AssignmentTypeInput';
import { NotDismissedEmployeeInput } from 'domain/shared/employee';
import EmployeeGroupDropdown from 'domain/shared/assignment/group/shared/input/EmployeeGroupDropdown';
import AssignmentTargetInput from 'domain/shared/assignment/target/shared/input/AssignmentTargetInput';
import AssignmentBudgetsDropdown from 'domain/shared/assignment/input/AssignmentBudgetsDropdown';
import { SPOF_TOOLTIP } from 'domain/shared/assignment/constants';
import useAssignmentFormState, {
  isIndividualAssignment,
  isGroupAssignment,
} from 'domain/shared/assignment/createupdate/useAssignmentFormState';
import EmployeeAssignmentMembershipInfo from './EmployeeAssignmentMembershipInfo';

const EmployeeAssignmentForm = ({ isCreateForm }: { isCreateForm?: boolean }) => {
  const {
    hasInitialEmployeeId,
    hasInitialEmployeeGroup,
    isGroupAssignmentsEnabled,
    isSpofVisible,
    isSecondaryLeadVisible,
    isStartedAssignment,
  } = useAssignmentFormState();
  return (
    <>
      <FormSection>
        <MediumFormRow>
          <AssignmentTypeInput
            source="assignmentType"
            isVisible={isGroupAssignmentsEnabled && !hasInitialEmployeeId && !hasInitialEmployeeGroup && isCreateForm}
          />
        </MediumFormRow>
        <FormRow>
          <NotDismissedEmployeeInput source="ownerId" label="Owner" nameSource="ownerName" />
          <ClearAfterUnmountWrapper isVisible={isIndividualAssignment}>
            <NotDismissedEmployeeInput
              source="employeeId"
              label="Worker"
              nameSource="employeeName"
              includeInterns
              includeContractors
              disabled={hasInitialEmployeeId || !isCreateForm}
            />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper isVisible={isGroupAssignment}>
            <EmployeeGroupDropdown
              source="employeeGroupId"
              label="Service"
              placeholder="Select service"
              errorSource="employeeGroup"
            />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper isVisible={isIndividualAssignment}>
            <DropdownInputOnlyBoolean placeholder="Bench" source="bench" label="Bench" />
          </ClearAfterUnmountWrapper>
        </FormRow>
        <FormRow>
          <AssignmentTargetInput
            mapResults={(target: any) => {
              return {
                id: target.id,
                text: target.displayName,
                target,
              };
            }}
            source="target"
            label="Target"
            nameSource="target.displayName"
            errorSource="rbsTreeNode"
            isVisible={assignment => !assignment?.target?.targetId}
          />
          <AssignmentServiceCatalogTargetInput
            mapResults={(target: any) => {
              return {
                id: target.id,
                text: target.name,
                target,
              };
            }}
            source="target"
            label="Service-catalog target"
            nameSource="name"
            isVisible={assignment => !assignment?.target?.id}
          />
          <TextInput
            source="target.budgetInfo.defaultBudget.name"
            label="Target's default budget"
            disabled
            isVisible={assignment => !!assignment?.target?.budgetInfo?.defaultBudget}
          />
          <AssignmentBudgetsDropdown
            source="budgetId"
            label="Budget"
            nameSource="budget.name"
            searchString="name"
            helpText="Specify budget if it differs from the default target budget"
            isVisible
          />
        </FormRow>
        <FormRow>
          <ClearAfterUnmountWrapper isVisible={isIndividualAssignment}>
            <RolesDropdown source="roleOnProject" label="Role" />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper isVisible={isIndividualAssignment}>
            <Checkbox source="backup" label="Backup" />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper isVisible={isSpofVisible}>
            <Checkbox source="isSpof" label="SPOF" tooltip={SPOF_TOOLTIP} />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper isVisible={isSecondaryLeadVisible}>
            <Checkbox source="isSecondaryLead" label="Secondary lead" />
          </ClearAfterUnmountWrapper>
        </FormRow>
        <FormRow>
          <ClearAfterUnmountWrapper isVisible={isIndividualAssignment}>
            <NotDismissedEmployeeInput
              source="backupForEmployeeId"
              label="Backup for"
              nameSource="backupForEmployeeName"
              errorSource="backupFor"
              isVisible={data => data.backup}
              includeInterns
              includeContractors
            />
          </ClearAfterUnmountWrapper>
        </FormRow>
        <FormRow>
          <DateInput source="from" disabled={!isCreateForm && isStartedAssignment} />
          <DateInput source="to" />
        </FormRow>
        <FormRow>
          <NumberInput source="minPercent" label="Min percent" integerOnly />
          <NumberInput source="maxPercent" label="Max percent" integerOnly />
        </FormRow>
      </FormSection>

      <EmployeeAssignmentMembershipInfo isCreateForm={isCreateForm} />
    </>
  );
};

export default EmployeeAssignmentForm;
